import React, { useState, useEffect, useContext } from "react";
import { useQuery, useMutation } from "react-apollo";
import {
  useParams,
  useHistory,
  useLocation,
  Route,
  Link,
  Switch
} from "react-router-dom";

import { Field, Control, Button, Icon, Tab, Title } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  SINGLE_LOCATION_QUERY,
  UPDATE_LOCATION_MUTATION,
  DELETE_LOCATION_MUTATION
} from "../../../graphql/Locations/Locations.graphql";

import Loading from "../../../components/Loading";

import { ModalContext } from "../../../components/ModalProvider";

import {
  AddLocationTankForm,
  AddLocationContactForm,
  LocationDirectionFilters
} from "../../../components/Locations";

import {
  AddDirectionForm,
  PrintDirections
} from "../../../components/Directions";

import Details from "./routes/Details";
import Contacts from "./routes/Contacts";
import Tanks from "./routes/Tanks";
import Directions from "./routes/Directions";
import { getUTCFormattedDate, ourToast as toast } from "../../../utils";
import { DeleteButton } from "../../../components/inputs";

const INITIAL_STATE = {
  TimeZoneRecIdRef: "",
  LocationID: "",
  LocationName: "",
  Active: "",
  BillToRecIdRef: "",
  Address1: "",
  Address2: "",
  City: "",
  State: "",
  Zip: "",
  Country: "",
  NumberOfLoadingBays: "",
  NumberOfUnloadingBays: "",
  Scales: "",
  LastUsedDate: "",
  XATADataStation: "",
  SpecialExternalInstructions: "",
  InternalNotes: "",
  PickupLocation: "",
  DeliveryLocation: ""
};

function LocationPage() {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();

  const { setModalOpen } = useContext(ModalContext);

  const { data } = useQuery(SINGLE_LOCATION_QUERY, {
    variables: { where: { RecId: parseInt(id) } }
  });

  const [updateLocation, { loading }] = useMutation(UPDATE_LOCATION_MUTATION, {
    refetchQueries: ["SINGLE_LOCATION_QUERY", "ALL_LOCATIONS_QUERY"]
  });

  const [editing, setEditing] = useState(true);
  const [inputs, setInputs] = useState({ ...INITIAL_STATE });

  useEffect(() => {
    if (data?.Location) {
      setInputs(data.Location);
    }
  }, [data]);

  if (!inputs.RecId && data?.Location) {
    setInputs(data.Location);
  }
  const toggleEditing = () => setEditing(!editing);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    const updates = { [e.target.name]: value };
    if (e.target.name === "NumberOfLoadingBays") {
      updates.NumberOfUnloadingBays = value;
    }
    setInputs({ ...inputs, ...updates });
  };
  const handleSave = async e => {
    e.preventDefault();
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      await updateLocation({
        variables: {
          data: {
            ...obj,
            LastUsedDate: inputs.LastUsedDate
              ? getUTCFormattedDate(inputs.LastUsedDate)
              : null,
            BillToRecIdRef: inputs.BillToRecIdRef
              ? parseInt(inputs.BillToRecIdRef)
              : null,
            TimeZoneRecIdRef: parseInt(inputs.TimeZoneRecIdRef),
            NumberOfLoadingBays: inputs.NumberOfLoadingBays
              ? parseInt(inputs.NumberOfLoadingBays)
              : null,
            NumberOfUnloadingBays: inputs.NumberOfUnloadingBays
              ? parseInt(inputs.NumberOfUnloadingBays)
              : null
          },
          where: { RecId: parseInt(id) }
        }
      });
      setEditing(false);
      toast.success("Location updated successfully.");
    } catch (err) {
      toast.error("Error updating Location.");
    }
  };
  // FILTERS

  const [filters, setFilters] = useState({});
  const handleSetFilters = f => {
    setFilters({ ...f });
  };

  if (loading || !data?.Location) return <Loading />;

  const getActionButtons = () => {
    if (location.pathname.includes("contacts")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="success"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <AddLocationContactForm
                    id={inputs?.RecId}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Contact</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("tanks")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="success"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <AddLocationTankForm
                    id={inputs?.RecId}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Tank</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("directions")) {
      const where = Object.keys(filters).length
        ? { ...filters, DestinationLocationRecIdRef: parseInt(id) }
        : {
            DestinationLocationRecIdRef: parseInt(id)
          };
      return (
        <Field kind="group">
          <Control>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <PrintDirections
                    where={where}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="print" />
              </Icon>
              <span>Print Directions</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="danger"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <LocationDirectionFilters
                    filters={filters}
                    locationRecId={parseInt(id)}
                    onClose={() => setModalOpen(false)}
                    onFilter={handleSetFilters}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="filter" />
              </Icon>
              <span>Filter</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="success"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <AddDirectionForm
                    id={inputs?.RecId}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Direction</span>
            </Button>
          </Control>
        </Field>
      );
    }

    return (
      <Field kind="group">
        <Control>
          <Button
            color="primary"
            disabled={editing}
            size="small"
            onClick={toggleEditing}
          >
            <Icon>
              <FontAwesomeIcon icon="edit" />
            </Icon>
            <span>Edit</span>
          </Button>
        </Control>
        <Control>
          <DeleteButton
            mutation={DELETE_LOCATION_MUTATION}
            refetchQueries={["ALL_LOCATIONS_QUERY", "SINGLE_LOCATION_QUERY"]}
            size="small"
            variables={{ where: { RecId: parseInt(id) } }}
            onComplete={() => history.push("/dispatch/locations")}
          />
        </Control>
        <Control>
          <Button
            color="success"
            disabled={!editing}
            size="small"
            onClick={handleSave}
          >
            <Icon>
              <FontAwesomeIcon icon="save" />
            </Icon>
            <span>Save</span>
          </Button>
        </Control>
      </Field>
    );
  };
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={1}>
            {inputs?.LocationID} - {inputs?.LocationName}
          </Title>
        </div>
        <div className="page-head-end">{getActionButtons()}</div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("details")}
          as={Link}
          tabIndex="-1"
          to="details"
        >
          <span>Details</span>
        </Tab>
        <Tab
          active={location.pathname.includes("contacts")}
          as={Link}
          tabIndex="-1"
          to="contacts"
        >
          <span>Contacts</span>
        </Tab>

        <Tab
          active={location.pathname.includes("tanks")}
          as={Link}
          tabIndex="-1"
          to="tanks"
        >
          <span>Tanks</span>
        </Tab>
        <Tab
          active={location.pathname.includes("directions")}
          as={Link}
          tabIndex="-1"
          to="directions"
        >
          <span>Directions</span>
        </Tab>
      </Tab.Group>
      <div className="page-tab-container">
        <Switch>
          <Route path="/dispatch/locations/:id/details">
            <Details
              editing={editing}
              locationData={inputs}
              onChange={handleChange}
            />
          </Route>
          <Route path="/dispatch/locations/:id/contacts">
            <Contacts editing={editing} locationRecId={inputs?.RecId} />
          </Route>
          <Route path="/dispatch/locations/:id/tanks">
            <Tanks editing={editing} locationRecId={inputs?.RecId} />
          </Route>
          <Route path="/dispatch/locations/:id/directions">
            <Directions
              editing={editing}
              filters={filters}
              locationRecId={id}
            />
          </Route>
        </Switch>
      </div>
    </React.Fragment>
  );
}

LocationPage.propTypes = {};
export default LocationPage;
